import React, { useState, useEffect } from 'react';
import { getBranchForecast, BranchForecast, StockForecast } from '../../services/stockService';
import { X, TrendingUp, TrendingDown, AlertTriangle, Calendar, Activity, Target, BarChart3 } from 'lucide-react';

interface StockForecastModalProps {
  isOpen: boolean;
  onClose: () => void;
  branchId: number;
  branchName: string;
}

const StockForecastModal: React.FC<StockForecastModalProps> = ({
  isOpen,
  onClose,
  branchId,
  branchName,
}) => {
  const [forecastData, setForecastData] = useState<BranchForecast | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [daysAhead, setDaysAhead] = useState(30);
  const [criticalOnly, setCriticalOnly] = useState(true);
  const [selectedItem, setSelectedItem] = useState<StockForecast | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadForecast();
    }
  }, [isOpen, daysAhead, criticalOnly]);

  const loadForecast = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getBranchForecast(branchId, daysAhead, criticalOnly);
      setForecastData(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load forecast');
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'increasing':
        return <TrendingUp className="w-5 h-5 text-red-600" />;
      case 'decreasing':
        return <TrendingDown className="w-5 h-5 text-green-600" />;
      case 'stable':
        return <Activity className="w-5 h-5 text-blue-600" />;
      default:
        return <BarChart3 className="w-5 h-5 text-gray-600" />;
    }
  };

  const getConfidenceBadge = (confidence: string) => {
    const colors = {
      high: 'bg-green-100 text-green-800 border-green-300',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      low: 'bg-red-100 text-red-800 border-red-300',
    };
    return colors[confidence as keyof typeof colors] || colors.low;
  };

  const getUrgencyColor = (forecast: StockForecast) => {
    if (forecast.current_stock === 0) return 'border-red-500 bg-red-50';
    if (forecast.days_until_stockout && forecast.days_until_stockout < 7) return 'border-red-400 bg-red-50';
    if (forecast.reorder_recommended) return 'border-orange-400 bg-orange-50';
    if (forecast.predicted_stock_end < 0) return 'border-yellow-400 bg-yellow-50';
    return 'border-gray-200 bg-white';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <BarChart3 className="w-7 h-7 mr-3 text-blue-600" />
              Stock Forecast & Prediction
            </h2>
            <p className="text-sm text-gray-600 mt-1">{branchName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Controls */}
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Forecast Period
              </label>
              <select
                value={daysAhead}
                onChange={(e) => setDaysAhead(Number(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
                <option value={60}>60 days</option>
                <option value={90}>90 days</option>
              </select>
            </div>

            <div className="flex items-end">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={criticalOnly}
                  onChange={(e) => setCriticalOnly(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Critical Items Only
                </span>
              </label>
            </div>

            {forecastData && (
              <div className="ml-auto flex items-center space-x-4 text-sm">
                <div className="px-3 py-2 bg-blue-100 text-blue-800 rounded-lg">
                  <strong>{forecastData.total_items}</strong> items analyzed
                </div>
                <div className="px-3 py-2 bg-orange-100 text-orange-800 rounded-lg">
                  <strong>{forecastData.items_needing_reorder}</strong> need reorder
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Analyzing sales patterns...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <AlertTriangle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={loadForecast}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          ) : !forecastData || forecastData.forecasts.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No forecast data available</p>
              <p className="text-sm text-gray-500 mt-2">
                {criticalOnly 
                  ? 'All items are in good stock condition'
                  : 'Try changing the forecast period or filters'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {forecastData.forecasts.map((forecast) => (
                <div
                  key={forecast.item_id}
                  className={`border-2 rounded-lg p-4 transition-all cursor-pointer ${getUrgencyColor(forecast)} ${
                    selectedItem?.item_id === forecast.item_id ? 'ring-2 ring-blue-500' : ''
                  }`}
                  onClick={() => setSelectedItem(selectedItem?.item_id === forecast.item_id ? null : forecast)}
                >
                  {/* Item Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="font-semibold text-gray-900">{forecast.item_name}</h4>
                        <span className="text-sm text-gray-500">({forecast.sku})</span>
                        {getTrendIcon(forecast.trend)}
                        <span className={`px-2 py-1 text-xs font-semibold rounded border ${getConfidenceBadge(forecast.confidence)}`}>
                          {forecast.confidence.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 flex items-center">
                        {forecast.message}
                      </p>
                    </div>
                  </div>

                  {/* Metrics Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                    <div className="bg-white rounded p-2 border border-gray-200">
                      <div className="text-xs text-gray-600">Current Stock</div>
                      <div className={`text-lg font-bold ${
                        forecast.current_stock === 0 ? 'text-red-600' :
                        forecast.current_stock < forecast.min_stock ? 'text-orange-600' :
                        'text-green-600'
                      }`}>
                        {forecast.current_stock}
                      </div>
                    </div>

                    <div className="bg-white rounded p-2 border border-gray-200">
                      <div className="text-xs text-gray-600">Predicted End Stock</div>
                      <div className={`text-lg font-bold ${
                        forecast.predicted_stock_end < 0 ? 'text-red-600' :
                        forecast.predicted_stock_end < forecast.min_stock ? 'text-orange-600' :
                        'text-green-600'
                      }`}>
                        {Math.round(forecast.predicted_stock_end)}
                      </div>
                    </div>

                    <div className="bg-white rounded p-2 border border-gray-200">
                      <div className="text-xs text-gray-600">Daily Usage</div>
                      <div className="text-lg font-bold text-blue-600">
                        {forecast.adjusted_daily_usage.toFixed(1)}
                      </div>
                    </div>

                    <div className="bg-white rounded p-2 border border-gray-200">
                      <div className="text-xs text-gray-600">Days Until Out</div>
                      <div className={`text-lg font-bold ${
                        forecast.days_until_stockout === null ? 'text-gray-400' :
                        forecast.days_until_stockout < 7 ? 'text-red-600' :
                        forecast.days_until_stockout < 14 ? 'text-orange-600' :
                        'text-green-600'
                      }`}>
                        {forecast.days_until_stockout ?? '∞'}
                      </div>
                    </div>
                  </div>

                  {/* Reorder Recommendation */}
                  {forecast.reorder_recommended && (
                    <div className="bg-gradient-to-r from-orange-100 to-red-100 border border-orange-300 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Target className="w-5 h-5 text-orange-600 mr-2" />
                          <div>
                            <div className="font-semibold text-orange-900">Reorder Recommended</div>
                            <div className="text-sm text-orange-700">
                              Suggested order quantity: <strong>{forecast.recommended_order_qty}</strong> units
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Detailed Info (when selected) */}
                  {selectedItem?.item_id === forecast.item_id && (
                    <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      <div>
                        <span className="text-gray-600">Trend:</span>
                        <span className="ml-2 font-semibold capitalize">{forecast.trend}</span>
                        {forecast.trend_percentage !== 0 && (
                          <span className={`ml-1 ${forecast.trend_percentage > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            ({forecast.trend_percentage > 0 ? '+' : ''}{forecast.trend_percentage}%)
                          </span>
                        )}
                      </div>
                      <div>
                        <span className="text-gray-600">Avg Daily Usage:</span>
                        <span className="ml-2 font-semibold">{forecast.avg_daily_usage.toFixed(2)}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Predicted Usage:</span>
                        <span className="ml-2 font-semibold">{Math.round(forecast.predicted_usage)}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Min Stock:</span>
                        <span className="ml-2 font-semibold">{forecast.min_stock}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Reorder Point:</span>
                        <span className="ml-2 font-semibold">{Math.round(forecast.reorder_point)}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Data Period:</span>
                        <span className="ml-2 font-semibold">{forecast.days_of_data} days</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            <p>Click on items for detailed information</p>
            <p className="text-xs mt-1">Forecast based on last 90 days of sales data with trend analysis</p>
          </div>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default StockForecastModal;
